import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";

import { filterConversations } from "../../Helpers/filters/filterConversations";
import CallListElement from "../AudioVideoCalls/CallListElement";
import SearchMenu from "../GenericComponents/SearchMenu";
import CallListContextMenu from "./CallListContextMenu";
import DeleteConfirmationChild from "../GenericComponents/DeleteConfirmationChild";
import { useContextMenu } from "react-contexify";
import { useCallsService } from "../../Context/CallsContext";

import { useTranslate } from '@tolgee/react';

import { RotatingLines } from "react-loader-spinner";
import { colors } from "../../Assets/styles/colors";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import EmptyCallList from "./EmptyCallList";
import { useNotify } from "../../Helpers/Notifications";
import WebSocketObserver from "../../Helpers/websocket/WebSocketObserver";
import { useWebSocketDataService } from "../../Context/WebsocketContext";
import { useAuthService } from "../../Context/AuthContext";

const CallsList = ({
  searchInput,
  setSearchInput,
  calls,
  selectedCall,
  handleSelectCall,
  loadCalls,
  setLoading,
}) => {
  const [selectedContextMenuItem, setSelectedContextMenuItem] = useState(null);
  const { deleteCall } = useCallsService();

  const [blockedIds, setBlockedIds] = useState(new Set());
  
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const { callListSpinner } = useGenericComponentsStateService();
  const { notifySuccess, notifyError, notifyInfo } = useNotify();
  const { add_observer, remove_observer } = useWebSocketDataService();
  const { userRef } = useAuthService();
  const { t } = useTranslate();

  const MENU_ID = "callList-id";

  const { show } = useContextMenu({
    id: MENU_ID,
  });

  function displayMenu(e, call) {
    setSelectedContextMenuItem(call);
    show({ event: e });
  }

  const handleDeleteCall = () => {
    const item = selectedContextMenuItem;

    if (!item) {
      console.error("Delete Error: No item selected.");
      return;
    }

    const uiId = item.id;        
    const apiUuid = item.uuid;   

    console.log(`Blocking UI-ID: ${uiId} | Deleting UUID: ${apiUuid}`);

    setBlockedIds((prev) => {
      const next = new Set(prev);
      if (uiId) next.add(uiId);
      if (apiUuid) next.add(apiUuid);
      return next;
    });

    setLoading(true);

    if (apiUuid) {
      deleteCall(
        apiUuid,
        (response) => {
          setLoading(false);
          if (loadCalls) loadCalls();
        },
        (error) => {
          notifyError(t('general.something_wrong'));
          setLoading(false);
          
          setBlockedIds((prev) => {
            const next = new Set(prev);
            if (uiId) next.delete(uiId);
            if (apiUuid) next.delete(apiUuid);
            return next;
          });
          
          if (loadCalls) loadCalls();
        }
      );
    } else {
      console.error("Critical: No UUID available for API call");
      setLoading(false);
      
      setBlockedIds((prev) => {
        const next = new Set(prev);
        if (uiId) next.delete(uiId);
        return next;
      });
    }
  };

  const handleCallsWsResponse = useCallback((response) => {
    if (response.update === "close" || response.update === "open") {
      if (loadCalls) loadCalls();
    }

    if (
      response.update === "participant_answered" &&
      response.participant_id === userRef.current?.id
    ) {
      if (loadCalls) loadCalls();
    }
  }, [loadCalls, userRef]);

  useEffect(() => {
    const CallsChannelObserver = new WebSocketObserver(
      "CallsChannel",
      (message) => {
        handleCallsWsResponse(message);
      }
    );
    
    add_observer(CallsChannelObserver);
    return () => {
      remove_observer(CallsChannelObserver);
    };
  }, [add_observer, remove_observer, handleCallsWsResponse]);

  const visibleCalls = useMemo(() => {
    if (!calls) return [];
    
    const safeList = calls.filter(c => {
      if (c.id && blockedIds.has(c.id)) return false;
      if (c.uuid && blockedIds.has(c.uuid)) return false;
      return true;
    });
    
    return filterConversations(safeList, searchInput);
  }, [calls, searchInput, blockedIds]); 

  return (
    <ChatsList>
      <DeleteConfirmationChild
        setOpen={setDeleteConfirmationOpen}
        open={deleteConfirmationOpen}
        action={handleDeleteCall}
        item={selectedContextMenuItem?.uuid} 
        variant={"delete_call"}
      />

      <CallListContextMenu
        selectedContextMenuItem={selectedContextMenuItem}
        setDeleteConfirmationOpen={setDeleteConfirmationOpen}
      />

      <div style={{ padding: "16px" }}>
        <SearchMenu value={searchInput} setvalue={setSearchInput} />
      </div>

      {!callListSpinner ? (
        visibleCalls.length > 0 ? (
          visibleCalls.map((call) => (
            <CallListElement
              key={call.id}
              data={call}
              selected={selectedCall?.id === call.id}
              handleSelectCall={handleSelectCall}
              displayMenu={displayMenu}
            />
          ))
        ) : (
          <EmptyCallList />
        )
      ) : (
        <SpinnerWrapper>
          <RotatingLines
            strokeColor={colors.purple}
            strokeWidth={3}
            animationDuration="0.75"
            width={50}
            visible={true}
          />
        </SpinnerWrapper>
      )}
    </ChatsList>
  );
};

export default CallsList;

const ChatsList = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  width: 100%;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;